/* @jsxImportSource @emotion/react */

import React from 'react'
import { css } from '@emotion/react'
import classNames from 'classnames'
import { FormattedMessage, useIntl } from 'react-intl'
import { useData } from 'vike-react/useData'

import { ImgSanity } from '@slite/components'

import { WebsiteCard } from '../../components/Card/Card'
import { Section } from '../../components/Section'
import { SlitePage } from '../../components/SlitePage'
import { WebsiteText } from '../../components/WebsiteText'
import { useRoutes } from '../../routes'
import type { Data } from './+data'

export default function WebinarsIndexPage() {
  const intl = useIntl()
  const routes = useRoutes()
  const { webinars } = useData<Data>()

  const title = intl.formatMessage({
    id: 'webinars.index.title',
    defaultMessage: 'Slite Webinars',
  })

  const description = intl.formatMessage({
    id: 'webinars.index.description',
    defaultMessage:
      'Watch our webinars on knowledge management, company brains and AI agents that keep team knowledge true.',
  })

  return (
    <SlitePage
      canonicalUrl={routes.webinars.index()}
      background="moon-dust"
      title={title}
      description={description}
      ogImage="https://cdn.sanity.io/images/dnsgqii5/production/a2df3c37ffc5a2d9dd8383c981d3e4e2cc7c4bcf-2400x1254.jpg"
    >
      <Section
        background="moon-dust"
        className="text-align-center"
        containerBottomPadding={0}
      >
        <div className="grid-12">
          <div className="grid-span-8-large grid-offset-2-large grid-span-12 flexbox flexbox-column flexbox-align-center">
            <WebsiteText variant="display" className="margin-bottom-16">
              <FormattedMessage
                defaultMessage="Webinars about the future of team knowledge."
                id="webinars.index.header"
              />
            </WebsiteText>
          </div>
        </div>
      </Section>
      <Section background="moon-dust" containerTopPadding={40}>
        <div className="grid-12 grid-gap-32">
          {webinars.length === 0 ? (
            <div className="grid-span-12 text-align-center padding-vertical-48">
              <WebsiteText variant="body" className="shade-20">
                <FormattedMessage
                  defaultMessage="No webinars yet. Check back soon."
                  id="webinars.index.empty"
                />
              </WebsiteText>
            </div>
          ) : (
            webinars.map((webinar, index) => {
              const image = webinar.heroImage ?? webinar.openGraphImage
              const speakers = webinar.speakers ?? []
              return (
                <WebsiteCard
                  key={webinar._id}
                  href={routes.webinars.detail({ slug: webinar.slug.current })}
                  hoverOutline={false}
                  className={classNames(
                    'padding-horizontal-16 padding-top-16 padding-bottom-20 border-small border-alpha-shade-40 border-shade-05-on-hover',
                    index < 2
                      ? 'grid-span-6 grid-span-12-medium grid-span-10-small grid-offset-1-small'
                      : 'grid-span-4 grid-span-6-medium grid-span-10-small grid-offset-1-small'
                  )}
                >
                  {image && (
                    <div
                      className="radius-larger margin-bottom-16"
                      css={css`
                        aspect-ratio: 16 / 9;
                        overflow: hidden;
                      `}
                    >
                      <ImgSanity
                        src={image.url}
                        alt={image.alt ?? webinar.title}
                        className="width-full height-full"
                        css={css`
                          object-fit: cover;
                        `}
                      />
                    </div>
                  )}
                  <p className="font-size-8 shade-20 margin-bottom-8">
                    {intl.formatDate(webinar.date, {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </p>
                  <h5 className="font-family-title font-size-4 font-weight-medium margin-bottom-4 padding-bottom-2 shade-05">
                    {webinar.title}
                  </h5>
                  {speakers.length > 0 && (
                    <p className="font-size-7 shade-10">
                      <FormattedMessage
                        id="webinars.index.speakers"
                        defaultMessage="With {speakers}"
                        values={{
                          speakers: intl.formatList(
                            speakers.map((speaker) => speaker.name),
                            { type: 'conjunction' }
                          ),
                        }}
                      />
                    </p>
                  )}
                </WebsiteCard>
              )
            })
          )}
        </div>
      </Section>
    </SlitePage>
  )
}
